import React, { useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { RESERVED_ORG_CODES } from '../App';
import Lottie from 'lottie-react';
import loginAnimation from '../assets/animations/login-animation.json';
import { Visibility, VisibilityOff } from '@mui/icons-material';

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, loginWithTotpToken } = useAuth();
  const orgContext = useOrg();
  const { orgCode: rawOrgCode } = useParams();
  // A bare `/login` also matches the app's `/:orgCode/*` route, with "login"
  // itself captured as orgCode — that's not a real org, so ignore it here too.
  const orgCode = rawOrgCode && !RESERVED_ORG_CODES.includes(rawOrgCode.toLowerCase()) ? rawOrgCode : undefined;
  const org = orgContext?.org;
  const orgPrefix = orgCode ? `/${orgCode}` : '';

  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // TOTP step state
  const [totpPending, setTotpPending] = useState(false);
  const [totpToken, setTotpToken] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.username || !formData.password) {
      setError(t('login.fillAllFields'));
      setLoading(false);
      return;
    }

    if (orgCode && (orgContext?.loading || orgContext?.error || !org)) {
      setError(orgContext?.error || t('common.loading'));
      setLoading(false);
      return;
    }

    const result = await login(formData.username, formData.password);
    setLoading(false);

    if (result.success) {
      // Redirect to org-prefixed home.  If the login was at a non-org URL
      // (e.g. plain /login), use the user's org_code from the response, or
      // fall back to the current orgPrefix, or just "/".
      let target = `${orgPrefix}/`;
      if (result.user?.org_code && !orgPrefix) {
        target = `/${result.user.org_code.toLowerCase()}/`;
        // Ensure apiClient picks up the org code for subsequent requests
        localStorage.setItem('orgCode', result.user.org_code.toUpperCase());
      }
      navigate(target);
    } else if (result.totpRequired) {
      setTotpToken(result.totpToken);
      setTotpPending(true);
    } else {
      const errorMessage = typeof result.error === 'object'
        ? (result.error?.non_field_errors?.join(', ') || JSON.stringify(result.error))
        : (result.error || t('login.loginFailed'));
      setError(errorMessage);
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      setError(t('login.enterSixDigit'));
      return;
    }
    setLoading(true);
    setError('');
    const result = await loginWithTotpToken(totpToken, totpCode);
    setLoading(false);
    if (result.success) {
      navigate(`${orgPrefix}/`);
    } else {
      setError(result.error || t('login.invalidCode'));
    }
  };

  const inputClass =
    'w-full px-4 py-3 border border-slate-200 rounded-xl text-[15px] bg-slate-50 ' +
    'transition-all duration-200 focus:outline-none focus:border-slate-900 focus:bg-white ' +
    'focus:ring-4 focus:ring-slate-900/10 hover:border-slate-300 ' +
    'disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed placeholder:text-slate-400';

  return (
    <div className="fixed inset-0 overflow-y-auto bg-gradient-to-br from-[#0f172a] to-[#1e293b] z-10">
      <div className="flex min-h-full items-center justify-center p-3 sm:p-5">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md px-5 py-8 sm:px-10 sm:py-12 border border-white/10 animate-slide-up">

          {/* Lottie */}
          <div className="flex justify-center mb-6 animate-fade-in">
            <Lottie animationData={loginAnimation} loop={true} style={{ width: 160, height: 160, maxWidth: '100%' }} />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            {org ? (
              <>
                <h2 className="text-[2rem] font-bold tracking-tight text-slate-900 mb-2">{org.name}</h2>
                <p className="text-sm font-semibold text-slate-500 mb-1">{org.organization_type} {t('login.portalSuffix')}</p>
                <p className="text-xs text-slate-400">
                  {org.city ? `${org.city} · ` : ''}{org.region || ''}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-[2rem] font-bold tracking-tight text-slate-900 mb-2">
                  {totpPending ? t('login.twoFactorTitle') : t('login.title')}
                </h2>
                <p className="text-sm font-medium text-slate-500">
                  {totpPending ? t('login.twoFactorSubtitle') : t('login.subtitle')}
                </p>
              </>
            )}
          </div>

          {/* Errors */}
          {orgContext?.error && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium animate-slide-down">
              {orgContext.error}
            </div>
          )}
          {error && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium animate-slide-down">
              {error}
            </div>
          )}

          {/* ── TOTP step ── */}
          {totpPending ? (
            <form onSubmit={handleTotpSubmit} className="flex flex-col gap-5">
              <div className="px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-700 text-sm">
                {t('login.twoFactorInfo')}
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="totp_code" className="text-sm font-semibold text-slate-800">{t('login.verificationCode')}</label>
                <input
                  type="text"
                  id="totp_code"
                  name="totp_code"
                  value={totpCode}
                  onChange={e => { setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  disabled={loading}
                  className={inputClass + ' text-center text-2xl tracking-[0.5em] font-bold'}
                />
              </div>
              <button
                type="submit" disabled={loading || totpCode.length !== 6}
                className="mt-1 w-full py-3 px-5 text-base font-bold text-white rounded-xl
                bg-gradient-to-br from-slate-900 to-slate-700 shadow-md
                transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl
                active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? t('login.verifying') : t('login.verify')}
              </button>
              <button
                type="button"
                onClick={() => { setTotpPending(false); setTotpToken(''); setTotpCode(''); setError(''); }}
                className="text-sm text-slate-500 underline text-center"
              >
                ← {t('common.back')}
              </button>
            </form>
          ) : (
            /* ── Normal login form ── */
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="username" className="text-sm font-semibold text-slate-800">{t('login.username')}</label>
                <input
                  type="text" id="username" name="username"
                  value={formData.username} onChange={handleChange}
                  placeholder={t('login.usernamePlaceholder')} required disabled={loading}
                  className={inputClass}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="password" className="text-sm font-semibold text-slate-800">{t('login.password')}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'} id="password" name="password"
                    value={formData.password} onChange={handleChange}
                    placeholder={t('login.passwordPlaceholder')} required disabled={loading}
                    className={inputClass + ' pr-11'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    disabled={loading}
                    tabIndex={-1}
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center bg-transparent border-0 p-0 leading-none text-slate-400 hover:text-slate-600 disabled:opacity-50 focus:outline-none"
                  >
                    {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                  </button>
                </div>
              </div>

              <button
                type="submit" disabled={loading || (!!orgCode && (orgContext?.loading || !!orgContext?.error || !org))}
                className="mt-1 w-full py-3 px-5 text-base font-bold text-white rounded-xl
                bg-gradient-to-br from-slate-900 to-slate-700 shadow-md
                transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl
                active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? t('login.signingIn') : t('login.signIn')}
              </button>
            </form>
          )}

          {/* Footer */}
          {!totpPending && (
            <div className="text-center mt-6 pt-6 border-t border-slate-100">
              <p className="text-sm text-slate-500 mb-2">
                <Link
                  to={`${orgPrefix}/forgot-password`}
                  className="text-slate-600 font-semibold no-underline hover:underline"
                >
                  {t('login.forgotPassword')}
                </Link>
              </p>
              <p className="text-sm text-slate-500">
                {t('login.noAccount')}{' '}
                <Link
                  to={`${orgPrefix}/register`}
                  className="text-slate-900 font-bold no-underline hover:underline"
                >
                  {t('login.register')}
                </Link>
              </p>
            </div>
          )}

          {/* Powered by */}
          <p className="text-center text-xs text-slate-400 mt-6">
            Powered by{' '}
            <span className="font-semibold text-slate-500">Elysian Systems</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
